import { Button, DialogActions, DialogTitle } from "@mui/material";
import React, { useState } from "react";
import styled from "styled-components";

import { ICollaborationInvite } from "common/interfaces";
import PresentationPermissionsController, { copyCollaborationInviteUrlToClipboard } from "js/controllers/PresentationPermissionsController";
import getLogger, { LogGroup } from "js/core/logger";
import { UserProfile } from "js/react/components/Avatar";
import { BeautifulDialog, DialogContent, ShowErrorDialog, ShowSnackBar } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";

const logger = getLogger(LogGroup.PERMISSIONS);

const DialogActionsBetween = styled(DialogActions)`
    && {
        justify-content: space-between;
    }
`;

export interface ResendInviteDialogProps {
    invite: ICollaborationInvite;
    closeDialog: () => void;
}

export function ResendInviteDialog({ invite, closeDialog }: ResendInviteDialogProps) {
    const [fetching, setFetching] = useState(false);

    const handleCopyInviteLink = async () => {
        setFetching(true);

        try {
            await copyCollaborationInviteUrlToClipboard(invite);
        } catch (err) {
            logger.error(err, "[ResendInviteDialog] Failed to copy invite link");
            ShowErrorDialog({ message: "Failed to copy invite link" });
        }

        setFetching(false);
    };

    const handleSendEmail = async () => {
        setFetching(true);

        try {
            await PresentationPermissionsController.addPresentationPermissionsForUsers({
                users: [{
                    uid: invite.inviteeUid,
                    email: invite.inviteeEmail,
                    permissions: { write: { object: invite.permissions.write, permissions: invite.permissions.write }, read: { object: true, permissions: true }, owner: false }
                }],
            });

            ShowSnackBar({ message: `Invitation resent to ${invite.inviteeEmail}` });
        } catch (err) {
            logger.error(err, "[ResendInviteDialog] Failed to resend invitation");
            ShowErrorDialog({ message: "Failed to resend invitation" });
        }

        setFetching(false);
    };

    return (
        <BeautifulDialog id="confirmInvite" closeDialog={closeDialog}>
            <FetchingClickShield visible={fetching} backgroundColor="white" />
            <DialogTitle>Resend invitation?</DialogTitle>
            <DialogContent>
                <UserProfile
                    profile={{
                        email: invite.inviteeEmail,
                        isPending: true
                    }}
                />
            </DialogContent>
            <DialogActionsBetween>
                <Button
                    onClick={handleCopyInviteLink}
                >
                    Copy Invite Link
                </Button>
                <Button
                    color="primary"
                    onClick={handleSendEmail}
                >
                    Send Email
                </Button>
            </DialogActionsBetween>
        </BeautifulDialog>
    );
}
